import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { HashLink as Link } from 'react-router-hash-link';
import { useHistory, withRouter } from 'react-router';
import Logger from '@utility/services/Logger';
import { ROLES, TAB_INDICES } from '@constants/accessibility';
import keyCodes from '@components/userDocuments/editDocument/editJournal/constants/keyCodes';
import breakpoints from '@styling/breakpoints';
import { white } from '@styling/theme';
import publicAccessController from '@utility/publicAccessController';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import SVG from '@components/userDocuments/editDocument/editJournal/toolset/hoc/SVG';
import route from '@constants/route';
import { Card, Input, Select, SelectItem } from '@heroui/react';
import breakpointsMinWidth from '@styling/breakpointsMinWidth';
import Actions from '../../../store/actions';
import { handleLogin, PostSignInCalls } from '../../signIn/handleSignIn';
import { handleSSOLogin } from './ssoLogin';
import SVGLogo from '../../../../../common/components/UI/Toolbar/LeftToolbar/Logo/svgLogo';
import { ButtonDiv, ErrorSpan } from './signInSignUp';
import { InputsWrapper, StyledLink } from './signIn';
import CustomButton from './components/button';
import { HR, Line, OR } from './newSignIn';

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  height: 100vh;
  overflow-y: scroll;
  grid-template-rows: auto auto 1fr;

  @media ${breakpoints.tabletSm} {
    grid-template-columns: repeat(8, 1fr);
  }
  @media ${breakpoints.mobileLg} {
    grid-template-columns: repeat(4, 1fr);
  }
  @media ${breakpoints.mobileSm} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const MarketingContainer = styled.div`
  grid-column: 1 / span 12;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 25px;
`;

export const LogoWrapper = styled.div`
  cursor: pointer;
`;

export const LogoContainer = styled.div`
  display: flex;
  position: unset;
`;

const ContainerDiv = styled.div`
  height: fit-content;
  background-color: ${white};
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  margin-top: 40px;
  width: 100%;
  @media ${breakpointsMinWidth.tabletSm} {
    margin-top: 45px;
  }
  @media ${breakpointsMinWidth.tabletLg} {
    margin-top: 55px;
  }
  @media ${breakpointsMinWidth.desktopSm} {
    margin-top: 65px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    margin-top: 75px;
  }
`;

const InstitutionDiv = styled.div`
  padding: 10px 0;
  text-align: left;
`;

const Logo = styled.img`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  border-radius: 4px;
  object-fit: cover;
`;

const HeadingDiv = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

export const InstitutionImg = styled.span`
  margin-right: 4px;
`;

const HeadingText = styled.span`
  font-size: 16px;
  color: #595959;
`;

export const LinkWrap = styled.div`
  a {
    color: #595959;
    text-decoration: none;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  padding: 0 24px;
  max-width: 420px;
  box-shadow: none;

  @media ${breakpointsMinWidth.tabletSm} {
    width: 420px;
    padding: 16px;
    --tw-shadow:
      0px 0px 15px 0px rgb(0 0 0 / 0.03), 0px 2px 30px 0px rgb(0 0 0 / 0.08),
      0px 0px 1px 0px rgb(0 0 0 / 0.3);
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      var(--tw-shadow);
    max-width: unset;
  }
  @media ${breakpointsMinWidth.tabletMd} {
    width: 440px;
  }
  @media ${breakpointsMinWidth.desktopSm} {
    width: 475px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    width: 522px;
  }
`;

const InstitutionSignin = (props) => {
  const [universitySelected, setUniversitySelected] = useState(null);
  const [institutionEmail, setInstitutionEmail] = useState('');
  const [institutionData, setInstitutionData] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const { user } = props;
    if (user?.token) {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    const loginOnEnter = (event) => {
      if (event.keyCode === keyCodes.ENTER) {
        event.preventDefault();
        event.stopPropagation();
        if (universitySelected) {
          handleNonSSOLogin();
        } else {
          getInstitutions();
        }
      }
    };
    document.addEventListener('keydown', loginOnEnter, false);
    return () => {
      document.removeEventListener('keydown', loginOnEnter, false);
    };
  });

  const handleUniversitySelect = (org) => {
    localStorage.setItem('sp', org.domain);
    setUniversitySelected(org);
    if (org.isSSO && !org.isExternalUser) {
      localStorage.setItem('sign-in-method', `SSO-${org.domain}`);
      handleSSOLogin({
        universityAuth: org.domain,
        provider: org.domain,
        url: org.url,
      });
    }
  };

  const handleInstutionLogin = async () => {
    const { updateUser } = props;
    if (showLoading || !password || !institutionEmail) return;
    const cleanEmail = institutionEmail.trim().toLowerCase();
    setShowLoading(true);
    let userObj;
    try {
      userObj = await handleLogin(cleanEmail, password, updateUser, false);
      setErrorMessage('');
    } catch (err) {
      setErrorMessage(err.message);
      setShowLoading(false);
    }
    if (userObj) {
      setShowLoading(false);
      setIsLoggedIn(true);
      PostSignInCalls({ ...props, user: userObj.user.user });
    }
    setShowLoading(false);
  };

  const handleNonSSOLogin = async () => {
    if (
      !institutionEmail?.trim() ||
      !password ||
      !universitySelected ||
      showLoading
    ) {
      return;
    }
    const provider = localStorage.getItem('sp');
    localStorage.setItem('sign-in-method', `NONSSO-${provider}`);
    setShowLoading(true);
    await handleInstutionLogin();

    setShowLoading(false);
  };

  const getInstitutions = async () => {
    if (!institutionEmail?.trim() || showLoading) {
      return;
    }
    setShowLoading(true);
    try {
      const res = await publicAccessController.institutionSignin({
        email: institutionEmail,
      });
      setInstitutionData(res);
      setShowLoading(false);
      if (res.status && res.data.length === 1) {
        handleUniversitySelect(res.data[0]);
      }
    } catch (err) {
      setShowLoading(false);
      Logger.LogError(err);
    }
  };
  const defaultRedirectTo = () => {
    const path =
      !localStorage.getItem('rurl') ||
      localStorage.getItem('rurl') === '/signin'
        ? route.APPS
        : localStorage.getItem('rurl');

    return <Redirect to={{ pathname: path }} />;
  };
  if (isLoggedIn) {
    return defaultRedirectTo();
  }
  const history = useHistory();
  return (
    <Container>
      <MarketingContainer>
        <LogoContainer>
          <LogoWrapper
            onClick={() => {
              window.open(process.env.REACT_APP_REDIRECT_SIGNOUT, '_blank');
            }}
          >
            <SVGLogo appType="CRA" pathname={window.location.pathname} isTop />
          </LogoWrapper>
        </LogoContainer>
      </MarketingContainer>
      <ContainerDiv data-testid="container-signIn">
        <StyledCard className="h-max mb-[50px]">
          <HeadingDiv>
            <HeadingText>Institution Login</HeadingText>
          </HeadingDiv>
          {universitySelected && (
            <div>
              <Logo src={universitySelected?.LogoUrl} height="40px" />
            </div>
          )}
          <InputsWrapper>
            <Input
              label="Email"
              placeholder="Enter your email"
              value={institutionEmail}
              onChange={(e) => {
                setInstitutionEmail(e.target.value);
                if (universitySelected && institutionData.data) {
                  setUniversitySelected(null);
                  setInstitutionData({});
                }
              }}
              isRequired
              data-testid="institution-email-signIn"
            />
          </InputsWrapper>

          {universitySelected &&
            !(
              universitySelected.isSSO && !universitySelected.isExternalUser
            ) && (
              <>
                <InputsWrapper>
                  <Input
                    label="Password"
                    placeholder="Enter your password"
                    isRequired
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    type="password"
                    data-testid="institution-password-signIn"
                  />
                </InputsWrapper>
                <ErrorSpan
                  data-testid="error-institution-not-found"
                  className="mt-[4px]"
                >
                  {' '}
                  {errorMessage}
                </ErrorSpan>
                <StyledLink isPassword className="mt-[4px] text-center">
                  <Link to="/forgotPassword" data-testid="forgotPassword">
                    {' '}
                    Forgot Password{' '}
                  </Link>
                </StyledLink>
              </>
            )}

          {institutionData.data?.length > 1 && (
            <InstitutionDiv id="institution">
              <Select
                label="Institution"
                placeholder="Select an institution"
                selectionMode="single"
                className="!p-0"
                classNames={{
                  listbox: '[&>*:last-child]:!m-0 !p-[0px] [&>ul]:!px-[4px]',
                }}
              >
                {institutionData.data.map((org) => (
                  <SelectItem
                    startContent={
                      <Logo src={org.icon} width="20px" height="20px" />
                    }
                    key={org.domain}
                    onClick={() => handleUniversitySelect(org)}
                    tabIndex={TAB_INDICES.ZERO}
                    onKeyDown={(e) => {
                      if (e.keyCode === keyCodes.ENTER) {
                        handleUniversitySelect(org);
                      }
                    }}
                  >
                    {org.name}
                  </SelectItem>
                ))}
              </Select>
            </InstitutionDiv>
          )}

          {institutionData.data && !institutionData.status && (
            <ErrorSpan
              data-testid="error-institution-not-found"
              className="mt-[4px]"
            >
              {' '}
              {institutionData.message}
            </ErrorSpan>
          )}
          <ButtonDiv className="!mt-[16px] tabletSm:!mt-[16px] tabletMd-!mt-[16px] desktopMd-!mt-[24px]">
            {universitySelected ? (
              <CustomButton
                onClick={handleNonSSOLogin}
                isLoading={showLoading}
                data-testid="button-signin-with-institution"
                role={ROLES.BUTTON}
                tabIndex={TAB_INDICES.ZERO}
                onKeyDown={(e) => {
                  if (e.keyCode === keyCodes.ENTER) {
                    handleNonSSOLogin();
                  }
                }}
                isDisabled={showLoading || !password || !institutionEmail}
                color="primary"
                variant="solid"
              >
                Login
              </CustomButton>
            ) : (
              <CustomButton
                onClick={() => getInstitutions()}
                isLoading={showLoading}
                data-testid="button-signin-with-institution"
                aria-label="button-signin-with-institution"
                role={ROLES.BUTTON}
                tabIndex={TAB_INDICES.ZERO}
                onKeyDown={(e) => {
                  if (e.keyCode === keyCodes.ENTER) {
                    getInstitutions();
                  }
                }}
                isDisabled={showLoading || !institutionEmail}
                color="primary"
                variant="solid"
              >
                Next
              </CustomButton>
            )}
          </ButtonDiv>
          <div className="py-[8px] desktopSm:py-[16px] desktopMd:py-[24px]">
            <HR>
              <Line />
              <OR>OR</OR>
              <Line />
            </HR>
          </div>
          <CustomButton
            onClick={() => {
              history.push('/signin');
            }}
            data-testid="backToLogin"
            aria-label="backToLogin"
          >
            View other options to Login{' '}
          </CustomButton>
        </StyledCard>
      </ContainerDiv>
    </Container>
  );
};
const mapDispatchToProps = (dispatch) => ({
  signOut: () => {
    dispatch(Actions.storeUserProfile.signOut());
  },
  updateUser: (...params) => {
    dispatch(Actions.storeUserProfile.updateUser(...params));
  },
  getUserInfo: async (...params) => {
    await dispatch(Actions.userInfoActions.getUserInfo(...params));
  },
  connectWebsocket: (...params) => {
    dispatch(Actions.websocketActions.connectWebsocket(...params));
  },
  addNotifications: (...params) => {
    dispatch(Actions.notificationActions.addNotifications(...params));
  },
  addNewNotification: (...params) => {
    dispatch(Actions.notificationActions.addNewNotification(...params));
  },
});
const mapStateToProps = (state) => ({
  user: state.user,
  webSocket: state.websocket.webSocket,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(InstitutionSignin));

export const InstitutionSVGLogo = () => {
  return (
    <SVG
      width="24"
      height="24"
      viewbox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_10932_47859)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.25 22C1.25 21.5858 1.58579 21.25 2 21.25L22 21.25C22.4142 21.25 22.75 21.5858 22.75 22C22.75 22.4142 22.4142 22.75 22 22.75L2 22.75C1.58579 22.75 1.25 22.4142 1.25 22Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6 11.25C6.41421 11.25 6.75 11.5858 6.75 12V18C6.75 18.4142 6.41421 18.75 6 18.75C5.58579 18.75 5.25 18.4142 5.25 18V12C5.25 11.5858 5.58579 11.25 6 11.25Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10 11.25C10.4142 11.25 10.75 11.5858 10.75 12V18C10.75 18.4142 10.4142 18.75 10 18.75C9.58579 18.75 9.25 18.4142 9.25 18V12C9.25 11.5858 9.58579 11.25 10 11.25Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14 11.25C14.4142 11.25 14.75 11.5858 14.75 12V18C14.75 18.4142 14.4142 18.75 14 18.75C13.5858 18.75 13.25 18.4142 13.25 18V12C13.25 11.5858 13.5858 11.25 14 11.25Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18 11.25C18.4142 11.25 18.75 11.5858 18.75 12V18C18.75 18.4142 18.4142 18.75 18 18.75C17.5858 18.75 17.25 18.4142 17.25 18V12C17.25 11.5858 17.5858 11.25 18 11.25Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.2819 2.75C9.60449 2.75 8.94404 2.96165 8.39284 3.35537L4.11098 6.41384C3.25689 7.0239 2.75 8.00888 2.75 9.05847V11C2.75 11.4142 2.41421 11.75 2 11.75C1.58579 11.75 1.25 11.4142 1.25 11V9.05847C1.25 7.52445 1.99084 6.08487 3.23912 5.19324L7.52098 2.13477C8.32658 1.55933 9.29185 1.25 10.2819 1.25H13.7181C14.7081 1.25 15.6734 1.55933 16.479 2.13477L20.7609 5.19324C22.0092 6.08487 22.75 7.52445 22.75 9.05847V11C22.75 11.4142 22.4142 11.75 22 11.75C21.5858 11.75 21.25 11.4142 21.25 11V9.05847C21.25 8.00888 20.7431 7.0239 19.889 6.41384L15.6072 3.35537C15.056 2.96165 14.3955 2.75 13.7181 2.75H10.2819Z"
          fill="#03000A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.25012 8C8.25012 7.58579 8.58591 7.25 9.00012 7.25H15.0001C15.4143 7.25 15.7501 7.58579 15.7501 8C15.7501 8.41421 15.4143 8.75 15.0001 8.75H9.00012C8.58591 8.75 8.25012 8.41421 8.25012 8Z"
          fill="#03000A"
        />
      </g>
      <defs>
        <clipPath id="clip0_10932_47859">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </SVG>
  );
};
